import http from './httpClient';

import type { ProductRequest } from '@/types/authTypes';

const Auth = {
  resetUserPassword: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/user/reset-password`, data);
  },

  activateUser: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/user/activate`, data);
  },

  updateUserPassword: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/user/change-password`, data);
  },

  resetUserPasswordLink: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/user/reset-password-link`, data);
  },

  fetchV1MarketplaceItems: () => {
    return http.get(`${import.meta.env.VITE_AUTH_V1_BASE}/marketplace-items`);
  },

  fetchV2Users: (params: unknown) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/users`, { params });
  },

  fetchV2UserByUuid: (uuid: string) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/users/${uuid}`);
  },

  fetchV2UsersByProductId: (productId: string) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/users/by-product/${productId}`);
  },

  validateVerificationToken: (verificationToken: string) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/users/token/${verificationToken}`);
  },

  createV2User: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/users`, data);
  },

  updateV2User: (data: Record<string, unknown> & { uuid: string }) => {
    return http.put(`${import.meta.env.VITE_AUTH_V2_BASE}/users/${data.uuid}`, data);
  },

  deleteV2User: (uuid: string) => {
    return http.delete(`${import.meta.env.VITE_AUTH_V2_BASE}/users/${uuid}`);
  },

  assignV2Product: (data: Record<string, unknown>) => {
    return http.put(`${import.meta.env.VITE_AUTH_V2_BASE}/users/assign-product`, data);
  },

  unassignV2Product: (data: Record<string, unknown>) => {
    return http.put(`${import.meta.env.VITE_AUTH_V2_BASE}/users/unassign-product`, data);
  },

  fetchV1UserResources: (uuid: string) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/users/${uuid}/resources`);
  },

  fetchV1Organizations: (params: Record<string, unknown>) => {
    return http.get(`${import.meta.env.VITE_AUTH_V1_BASE}/organizations`, { params });
  },

  fetchV1OrganizationById: (id: string) => {
    return http.get(`${import.meta.env.VITE_AUTH_V1_BASE}/organizations/${id}`);
  },

  createV1Organization: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V1_BASE}/organizations`, data);
  },

  updateV1Organization: (data: Record<string, unknown> & { _id: string }) => {
    return http.put(`${import.meta.env.VITE_AUTH_V1_BASE}/organizations/${data._id}`, data);
  },

  activateMarketplaceItem: (data: Record<string, unknown> & { _id: string }) => {
    return http.put(
      `${import.meta.env.VITE_AUTH_V1_BASE}/organizations/${data._id}/activate-marketplaceitem`,
      data
    );
  },

  fetchV1BusinessUnits: (params: Record<string, unknown>) => {
    return http.get(`${import.meta.env.VITE_AUTH_V1_BASE}/business-units`, { params });
  },

  fetchV1BusinessUnit: (id: string) => {
    return http.get(`${import.meta.env.VITE_AUTH_V1_BASE}/business-units/${id}`);
  },

  createV1BusinessUnit: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V1_BASE}/business-units`, data);
  },

  updateV1BusinessUnit: (data: Record<string, unknown> & { _id: string }) => {
    return http.patch(`${import.meta.env.VITE_AUTH_V1_BASE}/business-units/${data._id}`, data);
  },

  deleteV1BusinessUnit: (id: string) => {
    return http.delete(`${import.meta.env.VITE_AUTH_V1_BASE}/business-units/${id}`);
  },

  createApiKey: (password: string) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/users/api-key`, { password });
  },

  createV2Project: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/projects`, data);
  },

  fetchV2Projects: (params = {}, evaluation = true) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/projects`, {
      params: { ...params, evaluation },
    });
  },

  fetchV2ProjectById: (id: string) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/projects/${id}`);
  },

  updateV2Project: (data: Record<string, unknown> & { _id: string }) => {
    return http.patch(`${import.meta.env.VITE_AUTH_V2_BASE}/projects/${data._id}`, data);
  },

  deleteV2Project: (id: string) => {
    return http.delete(`${import.meta.env.VITE_AUTH_V2_BASE}/projects/${id}`);
  },

  createV2Product: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_AUTH_V2_BASE}/products/iaas`, data);
  },

  fetchV2Products: (params: Record<string, unknown>) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/products/iaas`, { params });
  },

  fetchV2MonitoringProducts: (params: Record<string, unknown>) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/products/monitoring`, { params });
  },

  fetchV2ProductById: (id: string, enrich = true) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/products/iaas/${id}`, {
      params: { enrich },
    });
  },

  updateV2Product: (data: ProductRequest) => {
    return http.patch(`${import.meta.env.VITE_AUTH_V2_BASE}/products/iaas/${data.id}`, data);
  },

  deleteV2Product: (id: string) => {
    return http.delete(`${import.meta.env.VITE_AUTH_V2_BASE}/products/iaas/${id}`);
  },

  fetchV2JuiceGroups: (params: Record<string, unknown>) => {
    return http.get(`${import.meta.env.VITE_AUTH_V2_BASE}/reports/juice-groups`, { params });
  },
};

export default Auth;
